import { useState, useEffect, useCallback, useMemo } from "react";
import { IAircraft, IAircraftType } from "@vmosolutions/oc-interfaces";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { formatFunction, parseFunction } from "./helpers";

interface IAircraftTypeSelectFieldProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

/**
 * Loop through all available aircraft and create groupings and options by aircraft type;
 * @param aircrafts -
 */
export const getAircraftTypes = (aircrafts: IAircraft[]): IAircraftType[] => {
  const aircraftTypeGroups: { [key: string]: IAircraftType } = {}; // Define the type

  aircrafts.forEach((AC: IAircraft) => {
    if (AC?.aircraftType?.title) {
      const ACType: string = AC.aircraftType.title;
      if (!aircraftTypeGroups[ACType]) {
        aircraftTypeGroups[ACType] = AC.aircraftType;
      }
    }
  });

  return Object.values(aircraftTypeGroups);
};

/**
 * Sepcialized wrapper for "built-in" oc aircraft type select field.
 * @param props -
 */
export const AircraftTypeSelectField = (props: IAircraftTypeSelectFieldProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  /////////////////////////
  // MANAGE AIRCRAFT TYPE LIST
  /////////////////////////
  const [aircraft, setAircraft] = useState<IAircraft[]>([]);
  const aircraftTypes: IAircraftType[] = useMemo(() => {
    return aircraft?.length ? getAircraftTypes(aircraft) : [];
  }, [aircraft]);

  useEffect(() => {
    const getAircraftAction = async (): Promise<void> => {
      //const notifications: INotifications = { dispatch, errorMessage: "No Aircraft found." };
      const response = await AmplifyAPIWrapper.Get<IAircraft[]>("/aircraft");
      if (Array.isArray(response)) setAircraft(response);
    };
    getAircraftAction();
  }, []);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, aircraftTypes, valueKey, isMulti),
    [aircraftTypes, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Aircraft Type Selector"}
      isMulti={isMulti}
      options={aircraftTypes}
      getOptionLabel={(type: IAircraftType) => `${type.description} - ${type.codeICAO}`}
      getOptionValue={(type: IAircraftType) => type._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      defaultValue={defaultValue}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select Aircraft Type${isMulti && "s"}`}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
